/**
 * @file src/pages/oAuthCallBack/AuthCallback.jsx
 *
 * Handles the OAuth redirect callback for the same-tab redirect flow.
 */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { getAuthenticatedUser } from "api/auth.api";
import { acceptInvitation } from "api/invitation.api";
import { QUERY_KEYS } from "config/queryKeys";
import { ROUTES } from "config/routes";

const PENDING_INVITE_TOKEN_KEY = "pendingInviteToken";

// ---------------------------------------------------------------------------
// AuthCallback
// ---------------------------------------------------------------------------

const AuthCallback = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState("Completing authentication...");

  useEffect(() => {
    const handleCallback = async () => {
      const params = new URLSearchParams(window.location.search);
      const success = params.get("success") === "true";
      const error = params.get("error");
      const destination = params.get("destination");

      if (error || !success) {
        setStatus("Authentication failed. Redirecting...");
        setTimeout(() => {
          navigate(ROUTES.LANDING, { replace: true });
        }, 1500);
        return;
      }

      try {
        setStatus("Verifying your session...");

        const response = await getAuthenticatedUser();

        if (response?.user) {
          queryClient.setQueryData(QUERY_KEYS.auth.me(), response);
        }

        const pendingInviteToken = sessionStorage.getItem(
          PENDING_INVITE_TOKEN_KEY,
        );

        if (pendingInviteToken) {
          try {
            setStatus("Accepting invitation...");

            await acceptInvitation(pendingInviteToken);

            sessionStorage.removeItem(PENDING_INVITE_TOKEN_KEY);

            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.auth.me() });
            queryClient.invalidateQueries({
              queryKey: QUERY_KEYS.companies.all,
            });
            queryClient.invalidateQueries({
              queryKey: QUERY_KEYS.memberships.all,
            });
            queryClient.invalidateQueries({
              queryKey: QUERY_KEYS.invitations.all,
            });

            setStatus("Invitation accepted. Redirecting...");
            navigate(ROUTES.COMPANY_SELECT, { replace: true });
            return;
          } catch (inviteError) {
            const inviteStatus =
              inviteError?.status || inviteError?.response?.status;

            setStatus(
              inviteStatus === 403
                ? "This invitation was sent to a different email address."
                : inviteError?.message || "Failed to accept invitation.",
            );

            setTimeout(() => {
              navigate(ROUTES.ACCEPT_INVITE, { replace: true });
            }, 2000);
            return;
          }
        }

        setStatus("Success! Redirecting...");

        let target = ROUTES.COMPANY_SELECT;

        if (destination === "select-company") {
          target = ROUTES.COMPANY_SELECT;
        } else if (destination === "onboarding") {
          target = ROUTES.COMPANY_ONBOARDING;
        }

        navigate(target, { replace: true });
      } catch {
        setStatus("Session verification failed. Redirecting to login...");
        setTimeout(() => {
          navigate(ROUTES.LANDING, { replace: true });
        }, 1500);
      }
    };

    handleCallback();
  }, [navigate, queryClient]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="inline-block w-10 h-10 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin mb-4" />
        <h2 className="text-lg font-semibold text-gray-900 mb-1">{status}</h2>
        <p className="text-sm text-gray-500">Please wait...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
